import { useEffect, useState } from 'react'
import { Icon } from '@/components/ds'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useSetupStatusContext } from '@/contexts/SetupStatusContext'
import { useGuideContext } from '@/contexts/GuideContext'
import { useUser } from '@/contexts/UserContext'
import { GUIDE_REGISTRY } from '@/lib/guide-registry'
import type { Importancia, SetupStatusItem, SetupStatusItemKey } from '@/types/setup-status'

/**
 * Bloque "Termina de configurar tu empresa" del Dashboard.
 *
 * Reemplaza al panel angosto de 320px que vivía en una columna lateral: los
 * primeros pasos son lo primero que hay que resolver en una cuenta nueva, así
 * que ocupan el ancho completo y arriba de todo, no un costado.
 *
 * Los pasos son DERIVADOS del estado real de los datos (hay vehículo → el paso
 * está listo). Por eso no existe ninguna acción de "marcar como hecho": si el
 * paso está cumplido el sistema ya lo sabe solo, y un checklist de cumplimiento
 * que se puede tildar a mano deja de ser evidencia de nada.
 *
 * Sólo se destacan los 3 pendientes más urgentes (el orden lo fija el backend
 * vía `order`); el resto queda plegado. Mostrar diez tarjetas iguales no
 * jerarquiza: convierte la lista en ruido y el usuario no sabe por dónde
 * empezar.
 */

interface CopyPaso {
  /**
   * Glifo de Material Symbols. La clave se llama `icon` a propósito: es el
   * patrón que reconoce `scripts/check-icon-subset.mjs`, y un glifo que el
   * checker no ve termina dibujándose como su propio nombre en letras.
   */
  icon: string
  /**
   * Por qué conviene hacerlo — una línea, en positivo: qué HABILITA.
   *
   * El backend sólo emite `consequence` para 3 de los 9 pasos, y en negativo
   * ("sin vehículos registrados no podrás…"). Acá vive la copia de producto
   * completa; `consequence` queda de respaldo si algún día llega una clave que
   * este mapa no conozca. El `label`, en cambio, SIEMPRE sale del API.
   */
  porQue: string
  /** Texto del botón que lleva al módulo donde se resuelve el paso. */
  cta: string
}

const COPY: Record<SetupStatusItemKey, CopyPaso> = {
  empresa_datos_completos: {
    icon: 'apartment',
    porQue:
      'El RUT, la actividad y el correo de la empresa se imprimen en cada evidencia que emites.',
    cta: 'Completar datos',
  },
  al_menos_un_area_real: {
    icon: 'account_tree',
    porQue:
      'Las áreas ordenan a tus trabajadores y son un destinatario válido al enviar capacitaciones.',
    cta: 'Definir áreas',
  },
  al_menos_un_cargo_custom: {
    icon: 'badge',
    porQue:
      'Los programas se asignan por cargo. Sin cargos propios no puedes elegir destinatarios.',
    cta: 'Configurar cargos',
  },
  al_menos_un_trabajador_extra: {
    icon: 'group_add',
    porQue:
      'Sin trabajadores cargados no hay a quién enviarle capacitaciones ni de quién guardar evidencia.',
    cta: 'Sumar trabajador',
  },
  owner_cargo_asignado: {
    icon: 'how_to_reg',
    porQue:
      'Tu cargo define qué capacitaciones te corresponden y qué puedes firmar como responsable.',
    cta: 'Asignar cargo',
  },
  al_menos_un_epp_personalizado: {
    icon: 'engineering',
    porQue:
      'Define qué EPP entrega tu empresa para poder registrar la entrega por trabajador.',
    cta: 'Configurar EPP',
  },
  al_menos_un_documento_empresa: {
    icon: 'folder_open',
    porQue:
      'Son el respaldo de la empresa ante fiscalización y la fuente de tus capacitaciones.',
    cta: 'Subir documentos',
  },
  al_menos_un_vehiculo: {
    icon: 'local_shipping',
    porQue:
      'Los vehículos con revisión y seguro al día evitan hallazgos en fiscalización.',
    cta: 'Registrar vehículo',
  },
  al_menos_una_maquinaria: {
    icon: 'precision_manufacturing',
    porQue: 'Cada maquinaria exige mantención vigente y operadores capacitados.',
    cta: 'Registrar maquinaria',
  },
}

const IMPORTANCIA_LABEL: Record<Importancia, string> = {
  esencial: 'Esencial',
  recomendado: 'Recomendado',
  opcional: 'Opcional',
}

/** Cuántos pendientes se destacan como tarjeta antes de plegar el resto. */
const DESTACADOS = 3

function claveLS(empresaId: number | null): string | null {
  return empresaId === null ? null : `fc-primeros-pasos-collapsed-${empresaId}`
}

function leerOculto(empresaId: number | null): boolean {
  const clave = claveLS(empresaId)
  if (!clave) return false
  try {
    return localStorage.getItem(clave) === 'true'
  } catch {
    return false
  }
}

function guardarOculto(empresaId: number | null, oculto: boolean): void {
  const clave = claveLS(empresaId)
  if (!clave) return
  try {
    localStorage.setItem(clave, String(oculto))
  } catch {
    // Cuota llena o modo privado: la preferencia se pierde, la vista no.
  }
}

function plural(n: number, singular: string, plural: string): string {
  return n === 1 ? singular : plural
}

export function ConfiguracionInicialCard() {
  const { status, isLoading, error } = useSetupStatusContext()
  const { launchGuide } = useGuideContext()
  const { activeEmpresaId } = useUser()

  const [oculto, setOculto] = useState<boolean>(() => leerOculto(activeEmpresaId))
  const [verResto, setVerResto] = useState(false)
  const [verHechos, setVerHechos] = useState(false)

  // La preferencia de ocultar es POR EMPRESA: al cambiar de contexto hay que
  // releerla, no arrastrar la de la empresa anterior.
  useEffect(() => {
    setOculto(leerOculto(activeEmpresaId))
  }, [activeEmpresaId])

  const cambiarOculto = (valor: boolean) => {
    setOculto(valor)
    guardarOculto(activeEmpresaId, valor)
  }

  if (isLoading) {
    return (
      <div className="bg-card flex flex-col gap-3 rounded-xl border px-4 pt-3.5 pb-[15px]">
        <Skeleton className="h-7 w-64" />
        <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(244px, 1fr))' }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-[136px] rounded-[10px]" />
          ))}
        </div>
      </div>
    )
  }

  // Silencio total si el endpoint falla: el dashboard no se rompe por el
  // checklist de onboarding.
  if (error || !status) return null

  const total = status.items.length
  const pendientes = status.items.filter((i) => !i.completed)
  const hechos = status.items.filter((i) => i.completed)
  const nHechos = hechos.length
  const pct = total > 0 ? Math.round((nHechos / total) * 100) : 0
  const completo = pendientes.length === 0

  // Franja plegada: la única forma de recuperar el bloque una vez ocultado.
  if (oculto) {
    return (
      <button
        type="button"
        onClick={() => cambiarOculto(false)}
        className="bg-card hover:border-border-strong flex items-center gap-2.5 rounded-[10px] border px-3.5 py-2.5 text-left transition-colors"
      >
        <Icon name="rocket_launch" size={16} className="text-meta" />
        <span className="text-[12.5px] text-body">Configuración inicial</span>
        <span className="fc-num text-meta text-[11.5px]">
          {nHechos} de {total} pasos listos
          {completo ? '' : ` · quedan ${pendientes.length}`}
        </span>
        <span className="flex-1" />
        <span className="text-primary-hover text-xs font-medium">Mostrar</span>
      </button>
    )
  }

  // Configuración terminada: se celebra una vez y se puede ocultar. No queda
  // un bloque grande vacío ocupando el lugar de lo que sí requiere atención.
  if (completo) {
    return (
      <div className="border-primary-border bg-primary-50 flex flex-wrap items-center gap-2.5 rounded-[10px] border px-3.5 py-[11px]">
        <Icon name="task_alt" size={18} className="text-primary-hover" />
        <span className="text-primary-strong text-[12.5px] font-semibold">
          Configuración inicial completa
        </span>
        <span className="text-primary-hover text-[11.5px]">
          Los {total} pasos están listos. Ya puedes trabajar con todo el sistema.
        </span>
        <span className="flex-1" />
        <Button variant="outline" size="xs" onClick={() => cambiarOculto(true)}>
          Ocultar
        </Button>
      </div>
    )
  }

  const destacados = pendientes.slice(0, DESTACADOS)
  const resto = pendientes.slice(DESTACADOS)

  return (
    <div className="bg-card flex flex-col gap-[13px] rounded-xl border px-4 pt-3.5 pb-[15px]">
      {/* Cabecera: qué falta, cuánto llevas y cómo sacarlo de la vista. */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="bg-primary-100 flex size-7 shrink-0 items-center justify-center rounded-[7px]">
          <Icon name="rocket_launch" size={16} className="text-primary-hover" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="fc-card-title">Termina de configurar tu empresa</span>
          <span className="text-meta text-[11.5px]">
            {pendientes.length === 1
              ? 'Queda un paso. Habilita algo que hoy no puedes hacer.'
              : `Quedan ${pendientes.length} pasos. Cada uno habilita algo que hoy no puedes hacer.`}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2.5">
          <div
            className="bg-border-subtle h-1.5 w-[104px] overflow-hidden rounded-full"
            role="progressbar"
            aria-valuenow={nHechos}
            aria-valuemin={0}
            aria-valuemax={total}
          >
            <div
              className="bg-primary h-full rounded-full transition-[width] duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="fc-num text-muted-foreground text-[11.5px] whitespace-nowrap">
            {nHechos}/{total}
          </span>
          <Button variant="ghost" size="xs" onClick={() => cambiarOculto(true)}>
            Ocultar
          </Button>
        </div>
      </div>

      {/* Los 3 pendientes de arriba. El primero es el siguiente a hacer. */}
      <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(244px, 1fr))' }}>
        {destacados.map((item, i) => (
          <TarjetaPaso
            key={item.key}
            item={item}
            siguiente={i === 0}
            onAccion={() => launchGuide(item.key)}
          />
        ))}
      </div>

      {/* El resto de los pendientes, plegado. */}
      {resto.length > 0 && (
        <div className="border-border-faint flex flex-col border-t pt-[11px]">
          <button
            type="button"
            onClick={() => setVerResto((v) => !v)}
            aria-expanded={verResto}
            className="flex w-fit items-center gap-1.5 pb-0.5"
          >
            <Icon name="radio_button_unchecked" size={16} className="text-soft" />
            <span className="text-xs text-body">
              {verResto ? 'Ocultar' : 'Ver'} los otros {resto.length}{' '}
              {plural(resto.length, 'paso', 'pasos')}
            </span>
            <Icon name={verResto ? 'expand_less' : 'expand_more'} size={16} className="text-soft" />
          </button>
          {verResto && (
            <ul className="mt-1 flex flex-col overflow-hidden rounded-[9px] border">
              {resto.map((item) => (
                <li
                  key={item.key}
                  className="border-border-faint bg-surface-muted flex items-center gap-2.5 border-b px-[11px] py-2.5 last:border-b-0"
                >
                  <Icon name={COPY[item.key]?.icon ?? "circle"} size={15} className="text-soft" />
                  <span className="fc-label fc-truncate min-w-0 flex-1">{item.label}</span>
                  <span
                    className={`shrink-0 text-[10px] font-semibold tracking-[0.08em] uppercase ${
                      item.importancia === 'esencial' ? 'text-primary-hover' : 'text-meta'
                    }`}
                  >
                    {IMPORTANCIA_LABEL[item.importancia]}
                  </span>
                  {GUIDE_REGISTRY[item.key] && (
                    <Button variant="outline" size="xs" onClick={() => launchGuide(item.key)}>
                      Ver guía
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Lo ya resuelto: se muestra como chips, no como tarjetas, porque no
          requiere ninguna acción — sólo confirma avance. */}
      {nHechos > 0 && (
        <div className="border-border-faint flex flex-col gap-2.5 border-t pt-[11px]">
          <button
            type="button"
            onClick={() => setVerHechos((v) => !v)}
            aria-expanded={verHechos}
            className="flex w-fit items-center gap-1.5"
          >
            <Icon name="check_circle" size={16} className="text-success" />
            <span className="text-xs text-body">
              {nHechos} {plural(nHechos, 'paso completado', 'pasos completados')}
            </span>
            <Icon name={verHechos ? 'expand_less' : 'expand_more'} size={16} className="text-soft" />
          </button>
          {verHechos && (
            <div className="flex flex-wrap gap-1.5">
              {hechos.map((item) => (
                <span
                  key={item.key}
                  className="bg-border-faint text-muted-foreground inline-flex h-6 items-center gap-[5px] rounded-full px-2.5 text-[11.5px] whitespace-nowrap"
                >
                  <Icon name="check" size={13} className="text-success" />
                  {item.label}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function TarjetaPaso({
  item,
  siguiente,
  onAccion,
}: {
  item: SetupStatusItem
  siguiente: boolean
  onAccion: () => void
}) {
  const copy = COPY[item.key]
  const porQue = copy?.porQue ?? item.consequence ?? ''
  const tieneRuta = Boolean(GUIDE_REGISTRY[item.key])

  return (
    <div
      className={`flex flex-col gap-[7px] rounded-[10px] border px-[13px] py-3 ${
        siguiente ? 'border-primary-border bg-primary-50' : 'bg-card'
      }`}
    >
      <div className="flex items-center gap-1.5">
        <Icon
          name={copy?.icon ?? "circle"}
          size={15}
          className={siguiente ? 'text-primary-hover' : 'text-soft'}
        />
        <span
          className={`text-[10px] font-semibold tracking-[0.08em] uppercase ${
            item.importancia === 'esencial' ? 'text-primary-hover' : 'text-meta'
          }`}
        >
          {IMPORTANCIA_LABEL[item.importancia]}
        </span>
        <span className="flex-1" />
        {siguiente && (
          <span className="bg-primary-200 text-primary-strong rounded-[5px] px-1.5 py-0.5 text-[10px] font-semibold tracking-[0.08em] uppercase">
            Siguiente
          </span>
        )}
      </div>

      <div className="text-[13.5px] leading-[1.3] font-semibold tracking-[-0.01em]">
        {item.label}
      </div>
      <div className="text-muted-foreground flex-1 text-[11.5px] leading-[1.45]">{porQue}</div>

      {tieneRuta && (
        <div className="flex items-center pt-0.5">
          <Button
            variant={siguiente ? 'default' : 'outline'}
            size="xs"
            className="h-[29px]"
            onClick={onAccion}
          >
            {copy?.cta ?? "Ir"}
            <Icon name="arrow_forward" size={15} />
          </Button>
        </div>
      )}
    </div>
  )
}
